import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, InputNumber, message, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

const createRowKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const normalizeStudentId = (value) => (value === null || value === undefined || value === '' ? undefined : String(value));

const normalizeRow = (item = {}, index = 0) => ({
  key: item.key || createRowKey(),
  id: item.id || null,
  role_name: item.role_name || item.roleName || '',
  duty_scope: item.duty_scope || item.dutyScope || '',
  student_id: normalizeStudentId(item.student_id ?? item.studentId),
  sort_order: Number.isFinite(Number(item.sort_order ?? item.sortOrder))
    ? Number(item.sort_order ?? item.sortOrder)
    : index + 1
});

const ClassCadreManagement = ({ classId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (classId) {
      fetchData();
    }
  }, [classId]);

  const tokenHeaders = useMemo(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, studentsRes, cadresRes] = await Promise.all([
        axios.get('/api/v2/class-cadres/roles/default', { headers: tokenHeaders }),
        axios.get(`/api/v2/class-cadres/students/${classId}`, { headers: tokenHeaders }),
        axios.get(`/api/v2/class-cadres/class/${classId}`, { headers: tokenHeaders })
      ]);

      const roleList = Array.isArray(rolesRes.data?.data) ? rolesRes.data.data : [];
      const studentList = Array.isArray(studentsRes.data?.data) ? studentsRes.data.data : [];
      const currentCadres = Array.isArray(cadresRes.data?.data?.cadres) ? cadresRes.data.data.cadres : [];

      setStudents(studentList);

      const currentMap = new Map();
      currentCadres.forEach((item, index) => {
        currentMap.set(item.role_name, normalizeRow(item, index));
      });

      const baseRows = roleList.map((role, index) => {
        const saved = currentMap.get(role.role_name);
        if (saved) {
          currentMap.delete(role.role_name);
          return normalizeRow(
            {
              ...role,
              ...saved,
              sort_order: saved.sort_order ?? role.sort_order ?? index + 1
            },
            index
          );
        }

        return normalizeRow(role, index);
      });

      const extraRows = Array.from(currentMap.values()).map((item, index) =>
        normalizeRow(item, baseRows.length + index)
      );

      setRows([...baseRows, ...extraRows]);
    } catch (error) {
      console.error('加载班干部管理数据失败:', error);
      message.error(error.response?.data?.message || '加载班干部管理数据失败');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (key, patch) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const addCustomRole = () => {
    setRows((prev) => [
      ...prev,
      normalizeRow(
        {
          role_name: '',
          duty_scope: '',
          student_id: undefined,
          sort_order: prev.length + 1
        },
        prev.length
      )
    ]);
  };

  const removeRow = (key) => {
    setRows((prev) =>
      prev
        .filter((row) => row.key !== key)
        .map((row, index) => ({
          ...row,
          sort_order: index + 1
        }))
    );
  };

  const handleSave = async () => {
    const payload = rows
      .map((row, index) => {
        const student = students.find((item) => String(item.id) === String(row.student_id));
        return {
          role_name: String(row.role_name || '').trim(),
          duty_scope: String(row.duty_scope || '').trim(),
          student_id: student?.id || null,
          student_name: student?.student_name || '',
          sort_order: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : index + 1,
          is_active: student ? 1 : 0
        };
      })
      .filter((row) => row.role_name && row.student_name);

    try {
      setSaving(true);
      const response = await axios.post(
        `/api/v2/class-cadres/class/${classId}/batch`,
        { cadres: payload },
        { headers: tokenHeaders }
      );

      message.success(response.data?.message || '班干部设置已保存');
      fetchData();
    } catch (error) {
      console.error('保存班干部设置失败:', error);
      message.error(error.response?.data?.message || '保存班干部设置失败');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '排序',
      dataIndex: 'sort_order',
      width: 88,
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(next) => updateRow(record.key, { sort_order: next || 1 })}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '岗位名称',
      dataIndex: 'role_name',
      width: 180,
      render: (value, record) => (
        <Input
          value={value}
          maxLength={20}
          placeholder="例如：班长"
          onChange={(e) => updateRow(record.key, { role_name: e.target.value })}
        />
      )
    },
    {
      title: '职责说明',
      dataIndex: 'duty_scope',
      render: (value, record) => (
        <Input
          value={value}
          maxLength={60}
          placeholder="用于欢迎页和班干部页展示"
          onChange={(e) => updateRow(record.key, { duty_scope: e.target.value })}
        />
      )
    },
    {
      title: '学生',
      dataIndex: 'student_id',
      width: 260,
      render: (value, record) => (
        <Select
          allowClear
          showSearch
          value={value}
          placeholder="选择学生"
          optionFilterProp="label"
          style={{ width: '100%' }}
          options={students.map((student) => ({
            value: String(student.id),
            label: `${student.student_name}${student.seat_number ? ` · 座号${student.seat_number}` : ''}`
          }))}
          onChange={(next) => updateRow(record.key, { student_id: next })}
        />
      )
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (_, record, index) => (
        <Button danger type="link" disabled={index < 8} onClick={() => removeRow(record.key)}>
          删除
        </Button>
      )
    }
  ];

  const assignedCount = rows.filter((row) => row.student_id).length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="班干部设置会同步到桌面客户端欢迎页和“班干部”页面。"
      />

      <Card
        size="small"
        extra={
          <Space>
            <Tag color="blue">已设置 {assignedCount} 个岗位</Tag>
            <Tag color="geekblue">学生总数 {students.length}</Tag>
          </Space>
        }
      >
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            刷新
          </Button>
          <Button icon={<PlusOutlined />} onClick={addCustomRole}>
            新增岗位
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            保存班干部设置
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="key"
        bordered
        pagination={false}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 980 }}
      />

      <Card size="small" title="展示说明">
        <Space direction="vertical" size={6}>
          <Text>1. 只会保存已经选择学生的岗位。</Text>
          <Text>2. 默认岗位可以直接改人选，也可以补充职责说明。</Text>
          <Text>3. 新增岗位适合录入你们班级自定义的班干部职位。</Text>
        </Space>
      </Card>
    </Space>
  );
};

export default ClassCadreManagement;
